import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Landmark, Globe2 } from "lucide-react";
import { PageHeader, Spinner } from "../components/common";
import Breadcrumbs from "../components/Breadcrumbs";
import { MissionCard, MissionMap, Disclaimer } from "./VisasHub";
import { SITE } from "../lib/site";
import api from "../lib/api";

export default function RegionMissions() {
  const { host, region } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null); setErr(false);
    api.get(`/visas/regions/${host}/${encodeURIComponent(region)}`).then((r) => {
      setData(r.data);
      document.title = `Embassies & Consulates in ${r.data.region} — Located & Serving | ${SITE.brandDomain}`;
    }).catch(() => setErr(true));
  }, [host, region]);

  if (err) return (
    <div className="max-w-7xl mx-auto px-5 py-20 text-center">
      <p className="text-muted-foreground">Region not found.</p>
      <Link to="/visas" className="text-primary font-700 hover:underline mt-2 inline-block">← Passports & Visas</Link>
    </div>
  );
  if (!data) return <Spinner full />;

  const hostName = data.host_country?.name || data.host_iso2;
  const all = [...data.located, ...data.serving];

  return (
    <div data-testid={`region-page-${data.host_iso2}-${data.region.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <PageHeader eyebrow={`${SITE.name} · Embassies & Consulates by Region`}
        title={`${data.region}, ${hostName}`} entityCode={data.host_iso2}
        subtitle={`Diplomatic missions located in ${data.region} and consulates elsewhere whose documented jurisdiction covers ${data.region}.`}>
        <Breadcrumbs className="mt-4 text-zinc-300" items={[
          { label: "Visas & Embassies", to: "/visas" },
          { label: hostName, to: data.host_country?.id ? `/visas/${data.host_country.id}` : "/visas" },
          { label: data.region },
        ]} />
      </PageHeader>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 space-y-12">
        {all.length > 0 && <MissionMap missions={all} height="360px" />}

        <section data-testid="region-located-section">
          <h2 className="font-heading font-700 text-lg md:text-lg mb-1 flex items-center gap-2">
            <Landmark className="w-5 h-5 text-[#0e7490]" /> Embassies &amp; consulates located in {data.region}
          </h2>
          {data.located.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {data.located.map((m) => <MissionCard key={m.id} m={m} />)}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mt-2">No verified diplomatic missions are physically located in {data.region} in our directory yet.</p>
          )}
        </section>

        <section data-testid="region-serving-section">
          <h2 className="font-heading font-700 text-lg md:text-lg mb-1 flex items-center gap-2">
            <Globe2 className="w-5 h-5 text-[#0e7490]" /> Consulates serving {data.region}
          </h2>
          <p className="text-sm text-muted-foreground">Missions located elsewhere whose officially documented jurisdiction includes {data.region}.</p>
          {data.serving.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {data.serving.map((m) => <MissionCard key={m.id} m={m} />)}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mt-2">No additional consulates list {data.region} in their documented jurisdiction — the national embassy usually handles matters for this region.</p>
          )}
        </section>

        <Disclaimer text={data.disclaimer} />
      </div>
    </div>
  );
}
